/**
 * Log Viewer Component
 *
 * Displays CloudWatch logs with support for:
 * - Log group selection
 * - Time range filtering (controlled by parent)
 * - Log level filtering
 * - Grouped view (by RequestId) and flat view modes
 * - Live polling for real-time updates
 * - Pagination for loading more logs
 */

import { useState, useMemo, useCallback } from 'react';
import { List, Layers } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LOG_GROUPS,
  TIME_RANGE_LABELS,
  type LogEvent,
  type TimeRange,
} from '@/types/dashboard';
import type { ViewMode, LogLevelFilter } from './types';
import { getLogGroupLabel } from './formatters';
import { groupLogsByRequestId } from './utils';
import { GroupedLogView } from './GroupedLogView';
import { FlatLogView } from './FlatLogView';

// =============================================================================
// Helper Components
// =============================================================================

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 flex-1" />
        </div>
      ))}
    </div>
  );
}

function getSortedLogGroups(): string[] {
  return [...LOG_GROUPS].sort((a, b) =>
    getLogGroupLabel(a).localeCompare(getLogGroupLabel(b)),
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface LogViewerProps {
  events: LogEvent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  timeRange: TimeRange;
  selectedLogGroup: string;
  onLogGroupChange: (group: string) => void;
}

export function LogViewer({
  events,
  isLoading,
  isLoadingMore,
  error,
  hasMore,
  loadMore,
  timeRange,
  selectedLogGroup,
  onLogGroupChange,
}: LogViewerProps) {
  // Local UI state only
  const [levelFilter, setLevelFilter] = useState<LogLevelFilter>('ALL');
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');

  // Computed values
  const requestGroups = useMemo(() => {
    if (viewMode !== 'grouped') return [];
    return groupLogsByRequestId(events);
  }, [events, viewMode]);

  const sortedLogGroups = useMemo(() => getSortedLogGroups(), []);

  const filteredEvents = useMemo(() => {
    if (levelFilter === 'ALL') return events;
    return events.filter((event) => event.level === levelFilter);
  }, [events, levelFilter]);

  // Handlers
  const handleLogGroupChange = useCallback(
    (value: string) => {
      onLogGroupChange(value);
    },
    [onLogGroupChange],
  );

  // Render
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <HeaderLeft />
        <HeaderRight
          selectedLogGroup={selectedLogGroup}
          sortedLogGroups={sortedLogGroups}
          onLogGroupChange={handleLogGroupChange}
          levelFilter={levelFilter}
          onLevelFilterChange={setLevelFilter}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </CardHeader>
      <CardContent>
        <LogContent
          error={error}
          isLoading={isLoading}
          events={events}
          filteredEvents={filteredEvents}
          requestGroups={requestGroups}
          viewMode={viewMode}
          levelFilter={levelFilter}
          timeRange={timeRange}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
        />
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Header Components
// =============================================================================

function HeaderLeft() {
  return (
    <div className="flex items-center gap-4">
      <CardTitle>System Logs</CardTitle>
      <span className="flex items-center gap-1 text-sm text-muted-foreground">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        Live
      </span>
    </div>
  );
}

interface HeaderRightProps {
  selectedLogGroup: string;
  sortedLogGroups: string[];
  onLogGroupChange: (value: string) => void;
  levelFilter: LogLevelFilter;
  onLevelFilterChange: (filter: LogLevelFilter) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

function HeaderRight({
  selectedLogGroup,
  sortedLogGroups,
  onLogGroupChange,
  levelFilter,
  onLevelFilterChange,
  viewMode,
  onViewModeChange,
}: HeaderRightProps) {
  return (
    <div className="flex items-center gap-2">
      {/* Log Group Selector */}
      <Select value={selectedLogGroup} onValueChange={onLogGroupChange}>
        <SelectTrigger className="w-[200px]" aria-label="Select log group">
          <SelectValue placeholder="Select Lambda" />
        </SelectTrigger>
        <SelectContent>
          {sortedLogGroups.map((group) => (
            <SelectItem key={group} value={group}>
              {getLogGroupLabel(group)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Level Filter */}
      <Select
        value={levelFilter}
        onValueChange={(value) => onLevelFilterChange(value as LogLevelFilter)}
      >
        <SelectTrigger className="w-[120px]" aria-label="Filter by level">
          <SelectValue placeholder="All levels" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All Levels</SelectItem>
          <SelectItem value="ERROR">Error</SelectItem>
          <SelectItem value="WARN">Warning</SelectItem>
          <SelectItem value="INFO">Info</SelectItem>
        </SelectContent>
      </Select>

      {/* View Mode Toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex rounded-md border">
            <Button
              variant={viewMode === 'grouped' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-r-none border-0"
              onClick={() => onViewModeChange('grouped')}
              aria-label="Grouped view"
            >
              <Layers className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'flat' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-l-none border-0"
              onClick={() => onViewModeChange('flat')}
              aria-label="Flat view"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{viewMode === 'grouped' ? 'Grouped by Request' : 'Flat list'}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// =============================================================================
// Content Components
// =============================================================================

interface LogContentProps {
  error: string | null;
  isLoading: boolean;
  events: LogEvent[];
  filteredEvents: LogEvent[];
  requestGroups: ReturnType<typeof groupLogsByRequestId>;
  viewMode: ViewMode;
  levelFilter: LogLevelFilter;
  timeRange: TimeRange;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

function LogContent({
  error,
  isLoading,
  events,
  filteredEvents,
  requestGroups,
  viewMode,
  levelFilter,
  timeRange,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: LogContentProps) {
  // Error state
  if (error) {
    return (
      <div
        className="h-[300px] flex items-center justify-center text-destructive"
        role="alert"
      >
        {error}
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-[300px]">
        <LoadingSkeleton />
      </div>
    );
  }

  // Empty state
  if (events.length === 0) {
    return (
      <div
        className="h-[300px] flex items-center justify-center text-muted-foreground"
        role="status"
      >
        No logs in the {TIME_RANGE_LABELS[timeRange].toLowerCase()}
      </div>
    );
  }

  // Grouped view
  if (viewMode === 'grouped') {
    return (
      <div className="space-y-4">
        <ScrollArea className="h-[300px]">
          <GroupedLogView groups={requestGroups} levelFilter={levelFilter} />
        </ScrollArea>
        {hasMore && (
          <LoadMoreButton isLoading={isLoadingMore} onClick={onLoadMore} />
        )}
      </div>
    );
  }

  // Flat view - empty after filtering
  if (filteredEvents.length === 0) {
    return (
      <div
        className="h-[300px] flex items-center justify-center text-muted-foreground"
        role="status"
      >
        No {levelFilter.toLowerCase()} logs found
      </div>
    );
  }

  // Flat view with data
  return (
    <div className="space-y-4">
      <ScrollArea className="h-[300px]">
        <FlatLogView events={filteredEvents} />
      </ScrollArea>
      {hasMore && (
        <LoadMoreButton isLoading={isLoadingMore} onClick={onLoadMore} />
      )}
    </div>
  );
}

interface LoadMoreButtonProps {
  isLoading: boolean;
  onClick: () => void;
}

function LoadMoreButton({ isLoading, onClick }: LoadMoreButtonProps) {
  return (
    <div className="flex justify-center">
      <Button
        variant="outline"
        onClick={onClick}
        disabled={isLoading}
        aria-label="Load more logs"
      >
        {isLoading ? 'Loading...' : 'Load More'}
      </Button>
    </div>
  );
}
